import { useId, useState } from 'react'
import { Server, SlidersHorizontal } from 'lucide-react'

import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { PageHeader } from '../shared/PageHeader'
import { RENTAL_PATH_COPY } from '../../core/rental'
import { formatUsdMicros } from '../../core/pricing'
import { RentalDoor } from './RentalDoor'
import { DraftResumeBar, type ResumableDraft } from './DraftResumeBar'

export interface RentStartPageProps {
  onNavigate?: ((path: string) => void) | undefined
  /** Fresh hosts with free CPU; `undefined` while unknown. */
  hostsWithCapacity?: number | undefined
  /** Lowest observed competitive reference price, per hour. Indicative only. */
  indicativeFromMicros?: bigint | null | undefined
  draft?: ResumableDraft | null | undefined
  onDiscardDraft?: (() => void) | undefined
}

function hostCountLine(count: number | undefined) {
  if (count === undefined) return undefined
  if (count === 0) return <span className="rental-door-live-caution">No host has free capacity right now</span>
  return <span className="rental-door-live-positive">{count === 1 ? '1 host' : `${count} hosts`} with free capacity</span>
}

/**
 * The rental front door: two products, and nothing else.
 *
 * Door A rents free capacity on a host the tenant picks, at the host's signed
 * posted quote. Door B is the competitive request: the tenant describes the
 * machine and hosts send offers. Whole-host and provider-posted custom rental
 * are deliberately absent (RENTAL-FRONT-DOOR-PLAN D1).
 *
 * Door A never shows a price. The catalog's reference price is a host's
 * competitive bid for a preset, not its posted rental rate, so it may only
 * appear on Door B, and only labelled indicative.
 */
export function RentStartPage({ onNavigate, hostsWithCapacity, indicativeFromMicros, draft, onDiscardDraft }: RentStartPageProps) {
  const [sharedOpen, setSharedOpen] = useState(false)
  const [discarded, setDiscarded] = useState(false)
  const sharedId = useId()

  // `discarded` only drives the announcement. The bar follows `draft`, which
  // the route clears after a discard, so a later draft (another wallet's)
  // still shows.
  const discard = () => {
    onDiscardDraft?.()
    setDiscarded(true)
  }

  return (
    <section className="rental-path-page rental-doors-page">
      <PageHeader
        eyebrow="Rent"
        title="How do you want to rent?"
        description="Pick a host yourself, or describe what you need and let hosts send offers."
      />

      {draft
        ? <DraftResumeBar draft={draft} onContinue={() => onNavigate?.(draft.resumePath)} onDiscard={discard} />
        : null}
      <p className="sr-only" role="status">{discarded ? 'Draft discarded' : ''}</p>

      <div className="rental-path-grid rental-doors-grid">
        <RentalDoor
          id="host"
          icon={Server}
          title="Pick a host"
          summary="Browse hosts, choose one, and rent its free capacity at the price it posts."
          facts={[
            { term: 'Price', detail: "The host's posted rate, as an exact quote you sign" },
            { term: 'Host', detail: <>You choose it <Badge tone="neutral">Shared machine</Badge></> },
          ]}
          live={hostCountLine(hostsWithCapacity)}
          action="Browse hosts"
          onChoose={() => onNavigate?.('/app/rent/hosts')}
        >
          <div className="rental-door-disclosure">
            <Button size="sm" variant="ghost" aria-expanded={sharedOpen} aria-controls={sharedId} onClick={() => setSharedOpen((open) => !open)}>
              What does shared mean?
            </Button>
            <p id={sharedId} className={sharedOpen ? 'field-help' : 'sr-only'}>
              {RENTAL_PATH_COPY['host-available'].guarantee}
            </p>
          </div>
        </RentalDoor>

        <RentalDoor
          id="describe"
          icon={SlidersHorizontal}
          title="Describe what you need"
          summary="Set the size and image, and hosts that can run it send you offers."
          facts={[
            { term: 'Price', detail: 'Offers from hosts; you choose one' },
            { term: 'Host', detail: 'You pick from the offers' },
          ]}
          live={indicativeFromMicros != null
            ? <>Recent offers from about {formatUsdMicros(indicativeFromMicros)}/hour (indicative)</>
            : undefined}
          action="Describe your machine"
          onChoose={() => onNavigate?.('/app/rent/configure?path=competitive')}
        >
          <p className="field-help rental-door-note">Nothing is reserved until you accept an offer.</p>
        </RentalDoor>
      </div>
    </section>
  )
}
